























































  import { Facet } from '@empathyco/x-types';
  import { Dictionary, map, objectFilter } from '@empathyco/x-utils';
  import { Component, Prop, Vue } from 'vue-property-decorator';
  import { Getter } from '../../../../components/decorators/store.decorators';
  import { xComponentMixin } from '../../../../components/x-component.mixin';
  import { toKebabCase } from '../../../../utils/string';
  import FacetsMixin from '../facets.mixin';
  import { facetsXModule } from '../../x-module';

  /**
   * Custom interface to provide a slot name to a Facet.
   *
   * @internal
   */
  interface RenderFacet {
    slotNameById: string;
    slotNameByModelName: string;
    facet: Facet;
  }

  /**
   * This component renders the list of facets stored in the Facets module. Facets can be rendered
   * differently based on their purpose and this can be achieved using the exposed slots:
   * - A default and required slot.
   * - A custom slot for each facet with the facetId as its name. This allows each facet to be
   * rendered differently based on its needs.
   *
   * @remarks It extends {@link FacetsMixin}.
   *
   * @public
   */
  @Component({
    mixins: [xComponentMixin(facetsXModule)]
  })
  export default class Facets extends FacetsMixin {
    /**
     * Animation component that will be used to animate the facets.
     *
     * @public
     */
    @Prop({ default: 'ul' })
    public animation!: Vue | string;

    /**
     * Discriminates the facets rendered by this component. It expects a string containing facets
     * ids, comma separated. This property will include or exclude facets based on its value.
     * The default value is an empty string and the component will render all existing facets.
     *
     * @remarks
     * To behave as a `include`, simply set the facets ids, comma separated:
     * `existingFacets=[{ brand: ... }, category: { ... }, color: { ... }, price: { ... }]`
     * `renderableFacets="brand, category"`
     *
     * The component will render brand and category facets.
     *
     * On the other hand, to simulate an `exclude` behaviour and exclude a facet from being
     * rendered, append a '!' before its id:
     * `existingFacets=[{ brand: ... }, category: { ... }, color: { ... }, price: { ... }]`
     * `renderableFacets="!brand,!price"`
     *
     * The component will render category and color facets.
     *
     * @public
     */
    @Prop()
    public renderableFacets!: string | undefined;

    /**
     * Dictionary of facets in the state.
     *
     * @internal
     */
    @Getter('facets', 'facets')
    public facets!: Record<Facet['id'], Facet>;

    /**
     * Transforms a dictionary of Facets including the slot name.
     *
     * @returns A dictionary of facets with the slot name.
     *
     * @internal
     */
    protected get mappedFacets(): Dictionary<RenderFacet> {
      return map(this.facetsToRender, (facetId, facet) => ({
        slotNameById: toKebabCase(facetId),
        slotNameByModelName: toKebabCase(facet.modelName),
        facet
      }));
    }

    /**
     * The facets to be rendered after filtering {@link Facets.facets} by
     * {@link Facets.renderableFacets} content.
     *
     * @returns The list of facets to be rendered.
     *
     * @internal
     */
    protected get facetsToRender(): Dictionary<Facet> {
      if (!this.renderableFacets) {
        return this.facets;
      } else {
        const excludedRegExp = /^!/;
        const facetIds: string[] = this.renderableFacets.split(',').map(facetId => facetId.trim());
        const included: string[] = [];
        const excluded: string[] = [];
        facetIds.forEach(facetId => {
          if (excludedRegExp.test(facetId)) {
            excluded.push(facetId.replace(excludedRegExp, ''));
          } else {
            included.push(facetId);
          }
        });

        return this.filterFacetsToRender(included, excluded);
      }
    }

    /**
     * Indicates if there are facets available to show.
     *
     * @returns True if there are facets available and false otherwise.
     * @internal
     */
    protected get hasFacets(): boolean {
      return !!Object.keys(this.facetsToRender).length;
    }

    /**
     * Filter facets dictionary retrieving those included and/or removing excluded.
     *
     * @param included - List of facets to render.
     * @param excluded - List of not renderable facets.
     *
     * @returns The filtered list of facets to render.
     *
     * @internal
     */
    private filterFacetsToRender(included: string[], excluded: string[]): Dictionary<Facet> {
      const hasAnyFacetIncluded = included.length > 0;
      return objectFilter(this.facets, facetKey => {
        const isIncluded = included.includes(String(facetKey));
        const isExcluded = excluded.includes(String(facetKey));

        return hasAnyFacetIncluded ? isIncluded && !isExcluded : !isExcluded;
      });
    }
  }
